import { Injectable } from '@angular/core';
import * as duckdb from '@duckdb/duckdb-wasm';

interface ArrowRow {
  toJSON: () => Record<string, unknown>;
}
interface ArrowField {
  name: string;
}
interface ArrowResultTable {
  toArray: () => ArrowRow[];
  schema?: {
    fields?: ArrowField[];
  };
}

export interface FileTypeDescriptor {
  id: 'csv' | 'tsv' | 'parquet' | 'json';
  label: string;
  description: string;
  extensions: string[];
}

export interface FileLoadResult {
  tableName: string;
  rowCount: number;
  fileName: string;
}

const SUPPORTED_FILE_TYPES: FileTypeDescriptor[] = [
  { id: 'csv', label: 'CSV', description: 'Comma-separated values (.csv)', extensions: ['csv'] },
  { id: 'tsv', label: 'TSV', description: 'Tab-separated values (.tsv)', extensions: ['tsv'] },
  { id: 'parquet', label: 'Parquet', description: 'Columnar storage (.parquet)', extensions: ['parquet'] },
  { id: 'json', label: 'JSON', description: 'Structured JSON records (.json)', extensions: ['json'] },
];

const UPLOAD_TABLE_NAME = 'uploaded_data';

export interface QueryExecutionResult {
  rows: Record<string, unknown>[];
  columns: string[];
  executionTimeMs: number;
}

@Injectable({ providedIn: 'root' })
export class HealthcareSqlPlaygroundService {
  private db: duckdb.AsyncDuckDB | null = null;
  private connection: duckdb.AsyncDuckDBConnection | null = null;
  private initializationPromise: Promise<void> | null = null;

  async executeQuery(sql: string): Promise<QueryExecutionResult> {
    const connection = await this.ensureConnection();
    const startedAt = performance.now();
    const resultTable = (await connection.query(sql)) as ArrowResultTable;
    const executionTimeMs = Math.round(performance.now() - startedAt);
    const data = resultTable.toArray();
    const rows = data.map((row) => row.toJSON());
    const schemaFields = resultTable.schema?.fields ?? [];
    const columns = schemaFields.length
      ? schemaFields.map((field) => field.name)
      : this.deriveColumns(rows);
    return { rows, columns, executionTimeMs };
  }

  getSupportedFileTypes(): FileTypeDescriptor[] {
    return SUPPORTED_FILE_TYPES.map((type) => ({ ...type, extensions: [...type.extensions] }));
  }

  async loadFromBlob(blob: Blob, fileName: string, customTableName?: string): Promise<FileLoadResult> {
    const file = new File([blob], fileName, { type: blob.type });
    return this.loadFile(file, customTableName);
  }

  async loadFile(file: File, customTableName?: string): Promise<FileLoadResult> {
    const descriptor = this.detectFileType(file.name);
    if (!descriptor) {
      const supportedList = this.getSupportedFileTypes().map((type) => type.label).join(', ');
      throw new Error(`Unsupported file type. Please upload one of: ${supportedList}.`);
    }
    const connection = await this.ensureConnection();
    if (!this.db || typeof this.db.registerFileBuffer !== 'function') {
      throw new Error('DuckDB is not ready to accept file uploads yet. Try again in a moment.');
    }
    const fileBuffer = new Uint8Array(await file.arrayBuffer());
    const virtualFilePath = this.buildVirtualFilePath(file.name);
    await this.db.registerFileBuffer(virtualFilePath, fileBuffer);
    const finalTableName = customTableName || UPLOAD_TABLE_NAME;
    const tableIdentifier = this.escapeIdentifier(finalTableName);
    const loadStatement = this.buildLoadStatement(descriptor.id, virtualFilePath, tableIdentifier);
    await connection.query(loadStatement);
    const countResult = (await connection.query(
      `SELECT COUNT(*)::BIGINT AS row_count FROM ${tableIdentifier}`
    )) as ArrowResultTable;
    const countRows = countResult.toArray().map((row) => row.toJSON());
    const rowCount = Number((countRows[0] && countRows[0]['row_count']) ?? 0);
    return { tableName: finalTableName, rowCount, fileName: file.name };
  }

  private async ensureConnection(): Promise<duckdb.AsyncDuckDBConnection> {
    if (this.connection) return this.connection;
    if (!this.initializationPromise) this.initializationPromise = this.initializeDuckDb();
    await this.initializationPromise;
    if (!this.connection) throw new Error('DuckDB connection is not available after initialization.');
    return this.connection;
  }

  private async initializeDuckDb(): Promise<void> {
    const availableBundles = duckdb.getJsDelivrBundles();
    const bundle = await duckdb.selectBundle(availableBundles);
    if (!bundle.mainWorker || !bundle.mainModule) {
      throw new Error('Unable to resolve DuckDB WASM bundle for the current platform.');
    }
    const workerUrl = URL.createObjectURL(
      new Blob([`importScripts("${bundle.mainWorker}");`], { type: 'application/javascript' })
    );
    try {
      const worker = new Worker(workerUrl);
      const logger = new duckdb.ConsoleLogger();
      this.db = new duckdb.AsyncDuckDB(logger, worker);
      await this.db.instantiate(bundle.mainModule, bundle.pthreadWorker ?? null);
      this.connection = await this.db.connect();
    } finally {
      URL.revokeObjectURL(workerUrl);
    }
  }

  private deriveColumns(rows: Record<string, unknown>[]): string[] {
    const firstRow = rows.at(0);
    return firstRow ? Object.keys(firstRow) : [];
  }

  private detectFileType(fileName: string): FileTypeDescriptor | undefined {
    const extension = fileName.split('.').pop()?.toLowerCase() ?? '';
    return SUPPORTED_FILE_TYPES.find((type) =>
      type.extensions.map((ext) => ext.toLowerCase()).includes(extension)
    );
  }

  private buildVirtualFilePath(fileName: string): string {
    const sanitizedName = fileName.replace(/[^a-zA-Z0-9_.-]/g, '_');
    return `uploads/${Date.now()}-${sanitizedName}`;
  }

  private buildLoadStatement(
    fileType: FileTypeDescriptor['id'],
    virtualFilePath: string,
    tableIdentifier: string
  ): string {
    const escapedPath = this.escapeStringLiteral(virtualFilePath);
    switch (fileType) {
      case 'csv':
      case 'tsv':
        return `CREATE OR REPLACE TABLE ${tableIdentifier} AS SELECT * FROM read_csv_auto('${escapedPath}');`;
      case 'parquet':
        return `CREATE OR REPLACE TABLE ${tableIdentifier} AS SELECT * FROM parquet_scan('${escapedPath}');`;
      case 'json':
        return `CREATE OR REPLACE TABLE ${tableIdentifier} AS SELECT * FROM read_json_auto('${escapedPath}');`;
      default:
        throw new Error('Unsupported file type encountered while preparing load statement.');
    }
  }

  private escapeIdentifier(identifier: string): string {
    return `"${identifier.replace(/"/g, '""')}"`;
  }

  private escapeStringLiteral(value: string): string {
    return value.replace(/'/g, "''");
  }
}
